import type { FlexibleDate, MntDate, Money, OperationsHistory } from '../api/checkVehicle';
import {
  formatEuro,
  formatLocalizedNumber,
  formatLocalizedUnit,
  localizeUnitText,
  type VehicleUnit,
} from './units';

export interface OwnerStats {
  ownerChangeCount: number | null;
  estimatedOwnerCount: number | null;
  currentOwnerSince: string | null;
  currentOwnerDuration: string | null;
}

const OWNER_CHANGE_CODES = new Set(['owner_change', 'owner_change_with_fee']);

// Aliases Intl.DisplayNames doesn't know: colloquial short forms and older
// Estonian registry spellings. Standard names in English or Estonian resolve
// through the Intl-built reverse map below.
const COUNTRY_TO_ISO2: Record<string, string> = {
  holland: 'NL',
  uk: 'GB',
  suurbritannia: 'GB',
  usa: 'US',
  'united states of america': 'US',
  lati: 'LV',
};

// Deprecated ISO-3166 codes (CLDR territory aliases). Intl.DisplayNames
// resolves them to the canonical country's name ("UK" -> "United Kingdom"),
// so without this skip they'd shadow the canonical code in the reverse map —
// and none of them render as a flag emoji.
const DEPRECATED_ISO2 = new Set([
  'AN', 'BU', 'CS', 'DD', 'DY', 'FX', 'HV', 'NH',
  'RH', 'SU', 'TP', 'UK', 'VD', 'YD', 'YU', 'ZR',
]);

// name (lowercased) -> ISO2, built from Intl.DisplayNames so every country
// resolves in the languages MNT actually sends (English) plus Estonian.
// Built lazily: ~1300 lookups, only paid when a name misses the alias map.
let reverseCountryNames: Map<string, string> | null = null;

function buildReverseCountryNames(): Map<string, string> {
  const map = new Map<string, string>();
  for (const locale of ['en', 'et']) {
    let names: Intl.DisplayNames;
    try {
      names = new Intl.DisplayNames([locale], { type: 'region', fallback: 'none' });
    } catch {
      continue;
    }
    for (let a = 65; a <= 90; a += 1) {
      for (let b = 65; b <= 90; b += 1) {
        const code = String.fromCharCode(a, b);
        if (DEPRECATED_ISO2.has(code)) continue;
        const name = names.of(code);
        if (name && name !== code) map.set(name.toLowerCase(), code);
      }
    }
  }
  return map;
}

function isoFromCountryName(lower: string): string | null {
  reverseCountryNames ??= buildReverseCountryNames();
  return COUNTRY_TO_ISO2[lower] ?? reverseCountryNames.get(lower) ?? null;
}

/** Resolves the English/Estonian country spellings MNT returns to ISO-2. */
export function countryCode(country: string | null | undefined): string | null {
  if (!country) return null;
  const clean = country.trim();
  const lower = clean.toLowerCase();
  if (lower.includes('eestis')) return 'EE';
  return (
    COUNTRY_TO_ISO2[lower] ??
    (/^[A-Za-z]{2}$/.test(clean) ? clean.toUpperCase() : isoFromCountryName(lower))
  );
}

export function dash(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
}

export function formatNumber(value: number | null | undefined, locale = 'et'): string {
  return formatLocalizedNumber(value, locale);
}

export function formatUnit(value: number | null | undefined, unit: VehicleUnit, locale = 'et'): string {
  return formatLocalizedUnit(value, unit, locale);
}

export function formatKm(value: number | null | undefined, locale = 'et'): string {
  return formatUnit(value, 'kilometer', locale);
}

export function formatKg(value: number | null | undefined, locale = 'et'): string {
  return formatUnit(value, 'kilogram', locale);
}

export function formatMm(value: number | null | undefined, locale = 'et'): string {
  return formatUnit(value, 'millimeter', locale);
}

export function formatKw(value: number | null | undefined, locale = 'et'): string {
  return formatUnit(value, 'kilowatt', locale);
}

export function formatCc(value: number | null | undefined, locale = 'et'): string {
  return formatUnit(value, 'cubicCentimeter', locale);
}

export function formatLitersFromCc(value: number | null | undefined, locale = 'et'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return formatUnit(value / 1000, 'liter', locale);
}

export function formatDate(value: MntDate | FlexibleDate | null | undefined): string {
  return dash(value?.raw ?? value?.iso ?? null);
}

export function formatMoney(value: Money | null | undefined, locale = 'et'): string {
  if (!value) return '-';
  if (value.amount !== null && value.amount !== undefined && value.currency === 'EUR') {
    return formatEuro(value.amount, locale);
  }
  if (value.raw) {
    const raw = `${value.raw}${value.currency && !value.raw.includes(value.currency) ? ` ${value.currency}` : ''}`;
    return localizeUnitText(raw, locale) ?? raw;
  }
  if (value.amount === null || value.amount === undefined) return '-';
  return `${formatNumber(value.amount, locale)} ${value.currency ?? 'EUR'}`;
}

export function formatBoolean(value: boolean | null | undefined, yes: string, no: string, unknown = '-'): string {
  if (value === true) return yes;
  if (value === false) return no;
  return unknown;
}

export function countryFlag(country: string | null | undefined): string {
  if (!country) return '-';
  const clean = country.trim();
  const iso = countryCode(clean);
  if (!iso) return clean;
  const first = iso.charCodeAt(0) - 65 + 0x1f1e6;
  const second = iso.charCodeAt(1) - 65 + 0x1f1e6;
  return String.fromCodePoint(first, second);
}

function monthsBetween(startIso: string, end: Date): number {
  const start = new Date(`${startIso}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) return 0;
  let months = (end.getUTCFullYear() - start.getUTCFullYear()) * 12;
  months += end.getUTCMonth() - start.getUTCMonth();
  if (end.getUTCDate() < start.getUTCDate()) months -= 1;
  return Math.max(0, months);
}

const ASSUMED_KM_PER_YEAR = 15_000;
const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

/**
 * Odometer estimate for "now": the last reported reading plus an assumed
 * 15 000 km/year for the time elapsed since that reading, rounded up to the
 * next full 1000 km. Returns null when there is no reading to project from.
 */
export function projectMileageKm(
  latestKm: number | null | undefined,
  lastReportedIso: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (latestKm === null || latestKm === undefined || Number.isNaN(latestKm)) return null;
  let km = latestKm;
  if (lastReportedIso) {
    const last = new Date(lastReportedIso);
    if (!Number.isNaN(last.getTime())) {
      const years = Math.max(0, (now.getTime() - last.getTime()) / MS_PER_YEAR);
      km += ASSUMED_KM_PER_YEAR * years;
    }
  }
  return Math.ceil(km / 1000) * 1000;
}

export function formatMonthDuration(totalMonths: number): string {
  if (totalMonths < 1) return '< 1 month';
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  if (years > 0 && months > 0) return `${years} years ${months} months`;
  if (years > 0) return `${years} years`;
  return `${months} months`;
}

export function deriveOwnerStats(history: OperationsHistory | null | undefined, now = new Date()): OwnerStats {
  if (!history?.section_present) {
    return {
      ownerChangeCount: null,
      estimatedOwnerCount: null,
      currentOwnerSince: null,
      currentOwnerDuration: null,
    };
  }

  const ownerChangeDates: string[] = [];
  let ownerChangeCount = 0;
  for (const entry of history.entries) {
    const hasOwnerChange = entry.operations.some((operation) => OWNER_CHANGE_CODES.has(operation.code));
    if (hasOwnerChange) {
      ownerChangeCount += entry.operations.filter((operation) => OWNER_CHANGE_CODES.has(operation.code)).length;
      if (entry.date.iso) ownerChangeDates.push(entry.date.iso);
    }
  }

  ownerChangeDates.sort();
  const latestChange = ownerChangeDates.at(-1) ?? null;
  return {
    ownerChangeCount,
    // Owners = changes + 1: even with zero recorded changes the vehicle has
    // had its first (original) owner.
    estimatedOwnerCount: ownerChangeCount + 1,
    currentOwnerSince: latestChange,
    currentOwnerDuration: latestChange ? formatMonthDuration(monthsBetween(latestChange, now)) : null,
  };
}
